/**
 * JoinChooserPage — Unified CARTAL entry point.
 * ─────────────────────────────────────────────
 * Presents two large choices — Personal or CARTAL Business — and then
 * hands the user off to the EXISTING /register flow with a query param
 * that pre-selects the correct role bucket. No backend, no API, no
 * auth-logic changes. iter197.
 */
import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { User, Building2, ArrowLeft, ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_carvo-market/artifacts/c5durw9k_B010DD54-BD83-49AB-A5A3-946EA832B9AF.png";

export default function JoinChooserPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [qs] = useSearchParams();
  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;

  const forward = () => {
    const passthrough = new URLSearchParams();
    const intent = qs.get("intent");
    const activity = qs.get("activity");
    const next = qs.get("next");
    if (intent) passthrough.set("intent", intent);
    if (activity) passthrough.set("activity", activity);
    if (next) passthrough.set("next", next);
    // iter259 — marker so RegisterPage knows the auth gate has been passed.
    passthrough.set("via", "join");
    return passthrough.toString();
  };
  
  const go = (type) => () => {
    const extra = forward();
    nav(`/register?type=${type}${extra ? `&${extra}` : ""}`);
  };

  return (
    <div dir={lang === "ar" ? "rtl" : "ltr"}
         className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10"
         data-testid="join-chooser-page">
      <div className="max-w-5xl w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="CARTAL" className="h-16 sm:h-20 w-auto" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#0a0a0a]">
            {t("join.title")}
          </h1>
          <p className="text-[#6b6b6b] mt-2 max-w-xl mx-auto text-sm sm:text-base">
            {t("join.subtitle")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
          {/* ── Personal card ─────────────────────────────────────── */}
          <button
            type="button"
            onClick={go("personal")}
            data-testid="join-personal-card"
            className="group text-start p-6 sm:p-8 rounded-3xl bg-white border-2 border-[#0a0a0a]/10 hover:border-[#c9a55c] hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#c9a55c]/30"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="w-14 h-14 rounded-2xl bg-[#fdf9ef] border border-[#c9a55c]/30 flex items-center justify-center">
                <User className="w-7 h-7 text-[#8a6f2e]" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-[#0a0a0a]/[0.05] text-[#525252]">
                {t("join.personal.tag")}
              </span>
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-medium text-[#0a0a0a] mt-5 mb-2">
              {t("join.personal.title")}
            </h2>
            <p className="text-sm text-[#525252] leading-relaxed">
              {t("join.personal.desc")}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#8a6f2e] group-hover:text-[#c9a55c]">
              {t("join.personal.cta")} <Arrow className="w-4 h-4" />
            </div>
          </button>

          {/* ── Business card — black premium look, gold accents ───── */}
          <button
            type="button"
            onClick={go("business")}
            data-testid="join-business-card"
            className="group relative text-start p-6 sm:p-8 rounded-3xl text-white overflow-hidden hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#c9a55c]/40"
            style={{ background: "linear-gradient(135deg,#141414 0%,#0a0a0a 55%,#050505 100%)" }}
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-90"
              style={{
                background:
                  "radial-gradient(circle at 20% 0%, rgba(201,165,92,0.18), transparent 60%), radial-gradient(circle at 100% 100%, rgba(232,200,115,0.08), transparent 55%)",
              }}
            />
            <div className="relative">
              <div className="flex items-start justify-between gap-3">
                <div className="w-14 h-14 rounded-2xl bg-white/[0.08] border border-[#c9a55c]/50 flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-[#e8c873]" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-[#c9a55c]/15 border border-[#c9a55c]/40" style={{ color: "#e8c873" }}>
                  {t("join.business.tag")}
                </span>
              </div>
              <h2 dir="ltr" className="font-display text-xl sm:text-2xl font-medium mt-5 mb-2 flex items-center gap-2 justify-start" style={{ color: "#ffffff" }}>
                <span className="tracking-wide" style={{ color: "#ffffff" }}>CARTAL</span>
                <span style={{ color: "#e8c873" }}>Business</span>
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: "#ffffff" }}>
                {t("join.business.desc")}
              </p>
              <ul className="mt-4 space-y-1.5 text-xs" style={{ color: "#ffffff" }}>
                <li className="inline-flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#e8c873]" /> <span style={{ color: "#ffffff" }}>{lang === "ar" ? "توثيق رسمي للنشاط" : "Official business verification"}</span></li>
                <li className="inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#e8c873]" /> <span style={{ color: "#ffffff" }}>{lang === "ar" ? "لوحة تحكم مخصصة وتحليلات" : "Dedicated dashboard & analytics"}</span></li>
              </ul>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#e8c873]">
                {t("join.business.cta")} <Arrow className="w-4 h-4" />
              </div>
            </div>
          </button>
        </div>

        <p className="text-center text-sm text-[#525252] mt-8">
          {t("join.already")}{" "}
          <Link to="/login" className="text-[#8a6f2e] font-semibold hover:text-[#c9a55c]" data-testid="join-signin-link">
            {t("join.signin")}
          </Link>
        </p>
      </div>
    </div>
  );
}